import { lazy, Suspense, type ReactElement } from 'react';

import { ensureNamespaces } from '@/lib/i18n/catalog-store.js';
import { PAGE_NAMESPACES } from '@/lib/i18n/namespaces.js';
import { useAuth } from '@/providers/AuthProvider.js';

import { RouteFallback } from './RouteFallback.js';

/**
 * `/` показывает РАЗНОЕ в зависимости от авторизации: гостю — лендинг, авторизованному —
 * кабинет. Так было в PHP (IndexController::index смотрел cookie `token`), так и остаётся.
 *
 * Без редиректа намеренно: адрес в строке браузера один и тот же. На `/` ведёт редирект
 * кампаний (`/go/:campaign`), и прыжок оттуда на другой URL ломал бы атрибуцию и ссылки.
 *
 * Лендинг и кабинет — разные чанки: гостю незачем грузить кабинет с рендером решений,
 * а кабинету незачем тянуть маркетинговую страницу.
 *
 * ⚠️ Вместе с чанком ждём и namespace каталога. Это единственное место, где ждать можно:
 * компоненты читают каталог синхронно, значит к их первому рендеру строки обязаны уже
 * лежать в сторе. Оба ожидания идут параллельно — сеть за чанком и сеть за строками
 * стартуют одновременно, а не одно после другого.
 */
/**
 * ⚠️ Лендинг ПЕРЕСТАЛ обходиться одним ядром каталога. Секция тарифов редизайна печатает
 * слово «դրամ» и подпись кнопки оплаты, а они живут в namespace `payment` — том же, из
 * которого их берёт модалка кабинета. Дублировать строку во второй namespace нельзя:
 * одна и та же надпись в двух местах разъедется при первой же правке.
 */
const LandingPage = lazy(async () => {
  const [module] = await Promise.all([
    import('@/features/landing/LandingPage.js'),
    ensureNamespaces(PAGE_NAMESPACES.landing),
  ]);
  return { default: module.LandingPage };
});
const DashboardPage = lazy(async () => {
  const [module] = await Promise.all([
    import('@/features/dashboard/DashboardPage.js'),
    ensureNamespaces(PAGE_NAMESPACES.dashboard),
  ]);
  return { default: module.DashboardPage };
});

export function HomeRoute(): ReactElement {
  const { status } = useAuth();

  if (status === 'loading') return <RouteFallback />;

  return (
    <Suspense fallback={<RouteFallback />}>
      {status === 'authenticated' ? <DashboardPage /> : <LandingPage />}
    </Suspense>
  );
}
